import React, { useEffect, useState, useCallback } from 'react';
import { purchaseAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CTSReconcileDialog } from '@/components/CTSReconcileDialog';
import { Scissors, Loader2, RefreshCw, CheckCircle2, Undo2 } from 'lucide-react';
import { EntityLink } from '@/components/EntityLink';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export default function CTSReconciliationPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [target, setTarget] = useState(null);
  const [undoingId, setUndoingId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        purchaseAPI.listCTSReconcilePending(),
        purchaseAPI.listCTSReconciliations(),
      ]);
      setPending(p.data?.pending || []);
      setHistory((h.data?.reconciliations || []).slice(0, 15));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUndo = async (reco) => {
    if (!window.confirm(`Undo this reconciliation? This restores ${reco.full_sheets} full sheet(s) and removes ${reco.total_pieces} cut pieces + ${reco.scrap_kg} Kg scrap.`)) return;
    setUndoingId(reco.id);
    try {
      const res = await purchaseAPI.undoCTSReconciliation(reco.id);
      toast.success(res.data?.message || 'Reconciliation reversed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to undo reconciliation');
    } finally { setUndoingId(''); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="cts-reconciliation-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Scissors className="w-6 h-6 text-amber-600" /> CTS Reconciliation</h1>
          <p className="text-sm text-muted-foreground mt-1">Convert full-sheet stock received against a Cut-to-Size PO into cut pieces (STORE) + scrap (SCRAP yard).</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="cts-reco-refresh">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Refresh
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            Pending conversion <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{pending.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground text-sm"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
          ) : pending.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm flex flex-col items-center gap-2" data-testid="cts-reco-empty">
              <CheckCircle2 className="w-6 h-6 text-emerald-500" /> No full-sheet stock awaiting CTS conversion.
            </div>
          ) : (
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-2 font-medium">PO</th>
                    <th className="text-left p-2 font-medium">Supplier</th>
                    <th className="text-left p-2 font-medium">Full-sheet item</th>
                    <th className="text-center p-2 font-medium">Received</th>
                    <th className="text-center p-2 font-medium">Reconciled</th>
                    <th className="text-center p-2 font-medium">Pending</th>
                    <th className="text-left p-2 font-medium">In stock</th>
                    <th className="text-center p-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((r, i) => (
                    <tr key={`${r.pr_id}-${r.item_id}`} className="border-b" data-testid={`cts-reco-row-${i}`}>
                      <td className="p-2 font-mono">{r.po_number}</td>
                      <td className="p-2">{r.supplier_name}</td>
                      <td className="p-2">
                        <div className="font-medium"><EntityLink type="item" id={r.item_id}>{r.item_name}</EntityLink></div>
                        <div className="text-[10px] text-muted-foreground font-mono">{r.item_unique_id}</div>
                      </td>
                      <td className="p-2 text-center font-mono">{r.received_sheets}</td>
                      <td className="p-2 text-center font-mono text-emerald-700">{r.reconciled_sheets}</td>
                      <td className="p-2 text-center font-mono text-rose-700 font-semibold">{r.pending_sheets}</td>
                      <td className="p-2 text-[10px]">
                        {(r.godowns_with_stock || []).length === 0
                          ? <span className="text-rose-600">none on hand</span>
                          : r.godowns_with_stock.map(g => `${g.godown_name}: ${g.quantity}`).join(', ')}
                      </td>
                      <td className="p-2 text-center">
                        <Button size="sm" className="h-6 text-[10px] px-2 bg-amber-600 hover:bg-amber-700"
                          disabled={(r.godowns_with_stock || []).length === 0}
                          onClick={() => setTarget(r)} data-testid={`cts-reco-convert-${i}`}>
                          <Scissors className="w-3 h-3 mr-1" /> Reconcile
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {history.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Recent reconciliations</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-2 font-medium">Date</th>
                    <th className="text-left p-2 font-medium">PO</th>
                    <th className="text-left p-2 font-medium">Item</th>
                    <th className="text-center p-2 font-medium">Sheets</th>
                    <th className="text-center p-2 font-medium">Pieces</th>
                    <th className="text-center p-2 font-medium">Scrap (Kg)</th>
                    <th className="text-left p-2 font-medium">By</th>
                    <th className="text-center p-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((r) => (
                    <tr key={r.id} className={`border-b ${r.reversed ? 'opacity-50' : ''}`}>
                      <td className="p-2">{(r.reconciled_at || '').slice(0, 10)}</td>
                      <td className="p-2 font-mono">{r.po_number}</td>
                      <td className="p-2">{r.item_name}</td>
                      <td className="p-2 text-center font-mono">{r.full_sheets}</td>
                      <td className="p-2 text-center font-mono">{r.total_pieces}</td>
                      <td className="p-2 text-center font-mono">{r.scrap_kg}</td>
                      <td className="p-2">{r.reconciled_by_name}</td>
                      <td className="p-2 text-center">
                        {r.reversed ? (
                          <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 border-slate-300">Reversed</Badge>
                        ) : isAdmin ? (
                          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 text-rose-700 hover:bg-rose-50"
                            onClick={() => handleUndo(r)} disabled={undoingId === r.id} data-testid={`cts-reco-undo-${r.id}`}>
                            {undoingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Undo2 className="w-3 h-3 mr-1" /> Undo</>}
                          </Button>
                        ) : <span className="text-muted-foreground text-[10px]">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <CTSReconcileDialog
        line={target}
        open={!!target}
        onClose={() => setTarget(null)}
        onDone={load}
      />
    </div>
  );
}
